/**
 * AI Colleagues Modal
 *
 * Modal-based interface for the AI assistant feature.
 * Triggered via Cmd+J from the command palette.
 * 
 * Features:
 * - Colleague selection
 * - Chat with AI colleagues
 * - Conversation history panel
 * - Auto-save and restore conversations
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Drawer, Button, Tooltip } from 'antd';
import { HistoryOutlined, CloseOutlined } from '@ant-design/icons';
import { useAIColleague, useConversationHistory, type ColleagueId, type AIColleagueContext, type HandoffSuggestion, type Message } from '@/hooks/ai-colleagues';
import { ColleagueSelector } from './ColleagueSelector';
import { ChatPanel } from './ChatPanel';
import { ConversationHistory } from './ConversationHistory';
import styles from './AIColleaguesModal.module.less';

type AIColleaguesModalProps = {
  open: boolean;
  onClose: () => void;
  /** Initial context from current page */
  initialContext?: AIColleagueContext;
  /** Pre-selected colleague */
  initialColleagueId?: ColleagueId;
  /** Initial message to send immediately */
  initialMessage?: string;
  /** Callback after initial message is consumed */
  onInitialMessageConsumed?: () => void;
};

type View = 'selector' | 'chat';
type SpecialistColleagueId = Exclude<ColleagueId, 'general'>;
type PendingHandoff = {
  colleagueId: SpecialistColleagueId;
  prompt: string;
  context: AIColleagueContext;
};

// Quick actions per colleague
const QUICK_ACTIONS: Record<ColleagueId, Array<{ label: string; prompt: string }>> = {
  alex: [
    { label: 'STR filing process', prompt: 'Walk me through the JFIU STR filing process' },
    { label: 'STR criteria', prompt: 'Does this case meet the threshold for STR filing?' },
    { label: 'EDD requirements', prompt: 'What are the EDD requirements under AMLO?' },
    { label: 'HKMA examination prep', prompt: 'How should we prepare for an HKMA examination?' }
  ],
  morgan: [
    { label: 'Explain risk categories', prompt: 'Can you explain the different risk categories in KYT screening?' },
    { label: 'Wallet architecture', prompt: 'Explain our hot/warm/cold wallet architecture' },
    { label: 'Mixer exposure guidance', prompt: 'How should I handle addresses with mixer exposure?' },
    { label: 'Gas optimization', prompt: 'How can we optimize gas fees for transactions?' }
  ],
  jordan: [
    { label: 'FPS vs CHATS', prompt: "What's the difference between FPS and CHATS?" },
    { label: 'Fee schedule V2', prompt: 'Explain how Fee Schedule V2 works and what to check before submitting a change.' },
    { label: 'Pricing setup checks', prompt: 'What should I verify before changing this fee or pricing setup?' },
    { label: 'Name mismatch handling', prompt: 'How should I handle bank account name mismatches?' },
    { label: 'Large deposit review', prompt: 'What should I check for large deposit reviews?' }
  ],
  sam: [
    { label: 'Professional apology template', prompt: 'How should I write a professional apology to a client?' },
    { label: 'Escalation criteria', prompt: 'When should I escalate a ticket?' },
    { label: 'Response best practices', prompt: 'What are best practices for client responses?' }
  ],
  riley: [
    { label: 'Common processes', prompt: 'What are the most common processes I should know?' },
    { label: 'Escalation paths', prompt: 'How do escalation paths work here?' },
    { label: 'Regulatory reporting', prompt: 'What are our regulatory reporting obligations?' }
  ],
  casey: [
    { label: 'Chargeback process', prompt: 'How do I handle a chargeback dispute?' },
    { label: 'Card limit guidance', prompt: 'How do I adjust card limits for a user?' },
    { label: 'Application review', prompt: 'What should I check when reviewing a card application?' }
  ],
  taylor: [
    { label: 'CRS reporting guide', prompt: 'What are our CRS reporting obligations?' },
    { label: 'Trust KYC requirements', prompt: 'What KYC documents do we need for a trust account?' },
    { label: 'Corporate UBO rules', prompt: 'How do we identify UBOs for corporate accounts?' }
  ],
  general: [
    { label: 'Help me with a case', prompt: "I need help with a case I'm working on" },
    { label: 'Process question', prompt: 'Can you help me understand a process?' },
    { label: 'General guidance', prompt: 'I have a general question about operations' }
  ]
};

export const AIColleaguesModal: React.FC<AIColleaguesModalProps> = ({
  open,
  onClose,
  initialContext,
  initialColleagueId,
  initialMessage,
  onInitialMessageConsumed
}) => {
  const [view, setView] = useState<View>(initialColleagueId ? 'chat' : 'selector');
  const [showHistory, setShowHistory] = useState(false);
  const [pendingHandoff, setPendingHandoff] = useState<PendingHandoff | null>(null);
  const initialMessageSentRef = useRef(false);
  const sessionInitializedRef = useRef(false);
  const lastSavedMessagesRef = useRef<number>(0);

  // Conversation history hook
  const {
    conversations,
    isLoading: historyLoading,
    refresh: refreshHistory,
    loadConversation,
    createConversation,
    saveMessages,
    archiveConversation,
    generateTitle,
    activeConversationId,
    setActiveConversationId
  } = useConversationHistory();

  const {
    messages,
    input,
    setInput,
    attachments,
    addAttachments,
    removeAttachment,
    isLoading,
    error,
    sendMessage,
    reload,
    stop,
    clearMessages,
    setMessages,
    colleagueId,
    setColleagueId,
    context,
    setContext,
    inputRef
  } = useAIColleague({
    colleagueId: initialColleagueId || 'general',
    context: initialContext,
    onFinish: async (message) => {
      // Auto-save messages after each response
      if (activeConversationId && messages.length > lastSavedMessagesRef.current) {
        const newMessages = messages.slice(lastSavedMessagesRef.current);
        // Include the final assistant message
        const allNewMessages = [...newMessages, message];
        try {
          await saveMessages(activeConversationId, allNewMessages);
          lastSavedMessagesRef.current = messages.length + 1;
          
          // Generate title from first message if not set
          if (messages.length <= 2 && !conversations.find(c => c.id === activeConversationId)?.title) {
            const firstUserMessage = messages.find(m => m.role === 'user');
            if (firstUserMessage) {
              await generateTitle(activeConversationId, firstUserMessage.content);
            }
          }
        } catch (err) {
          console.error('Failed to save messages:', err);
        }
      }
    }
  });

  // Handle initial message - auto-send when modal opens with a message
  useEffect(() => {
    if (open && initialMessage && !initialMessageSentRef.current) {
      initialMessageSentRef.current = true;
      sessionInitializedRef.current = true;
      const targetColleague = initialColleagueId || 'riley';
      setColleagueId(targetColleague);
      setView('chat');
      
      // Create a new conversation and send the message
      (async () => {
        try {
          const convId = await createConversation({
            colleagueId: targetColleague,
            contextType: initialContext?.type,
            contextId: initialContext?.id
          });
          setActiveConversationId(convId);
          lastSavedMessagesRef.current = 0;
          
          setTimeout(() => {
            setInput(initialMessage);
            sendMessage(initialMessage);
            onInitialMessageConsumed?.();
          }, 100);
        } catch (err) {
          console.error('Failed to create conversation:', err);
          // Fallback to ephemeral conversation
          setTimeout(() => {
            setInput(initialMessage);
            sendMessage(initialMessage);
            onInitialMessageConsumed?.();
          }, 100);
        }
      })();
    }
    if (!open) {
      initialMessageSentRef.current = false;
    }
  }, [open, initialMessage, initialColleagueId, initialContext, setColleagueId, setInput, sendMessage, onInitialMessageConsumed, createConversation, setActiveConversationId]);

  // Handle colleague selection
  const handleSelectColleague = useCallback(async (id: ColleagueId) => {
    setColleagueId(id);
    clearMessages();
    setPendingHandoff(null);
    setView('chat');
    
    // Create a new conversation
    try {
      const convId = await createConversation({
        colleagueId: id,
        contextType: context?.type,
        contextId: context?.id
      });
      setActiveConversationId(convId);
      lastSavedMessagesRef.current = 0;
    } catch (err) {
      console.error('Failed to create conversation:', err);
      // Continue with ephemeral conversation
      setActiveConversationId(null);
    }
  }, [setColleagueId, clearMessages, createConversation, context, setActiveConversationId]);

  const handleSwitchColleague = useCallback(async (suggestion: HandoffSuggestion) => {
    const prompt = suggestion.originalQuestion.trim();
    if (!prompt) {
      return;
    }

    const nextContext: AIColleagueContext = {
      ...(suggestion.pageContext ?? context ?? {}),
      handoff: suggestion
    };

    stop();
    setColleagueId(suggestion.targetColleagueId);
    setContext(nextContext);
    clearMessages();
    setInput('');
    setView('chat');
    setActiveConversationId(null);
    lastSavedMessagesRef.current = 0;

    try {
      const convId = await createConversation({
        colleagueId: suggestion.targetColleagueId,
        contextType: nextContext.type,
        contextId: nextContext.id
      });
      setActiveConversationId(convId);
    } catch (err) {
      console.error('Failed to create handoff conversation:', err);
      setActiveConversationId(null);
    }

    setPendingHandoff({ colleagueId: suggestion.targetColleagueId, prompt, context: nextContext });
  }, [stop, setColleagueId, setContext, clearMessages, setInput, setActiveConversationId, createConversation, context]);

  useEffect(() => {
    if (!pendingHandoff || colleagueId !== pendingHandoff.colleagueId || isLoading) {
      return;
    }

    const prompt = pendingHandoff.prompt;
    const handoffContext = pendingHandoff.context;
    setPendingHandoff(null);
    setInput(prompt);
    void sendMessage(prompt, handoffContext);
  }, [pendingHandoff, colleagueId, isLoading, setInput, sendMessage]);

  // Handle loading a conversation from history
  const handleLoadConversation = useCallback(async (conversation: { id: string; colleagueId: string }) => {
    const loaded = await loadConversation(conversation.id);
    if (loaded) {
      setColleagueId(loaded.colleagueId as ColleagueId);
      setActiveConversationId(loaded.id);
      lastSavedMessagesRef.current = loaded.messages.length;
      
      // Convert messages to the hook format
      const hookMessages: Message[] = loaded.messages.map(m => ({
        id: m.id,
        role: m.role as 'user' | 'assistant' | 'system',
        content: m.content,
        createdAt: m.createdAt,
        toolCalls: m.toolCalls as Message['toolCalls'],
        toolResults: m.toolResults as Message['toolResults']
      }));
      
      setMessages(hookMessages);
      setPendingHandoff(null);
      setView('chat');
      setShowHistory(false);
    }
  }, [loadConversation, setColleagueId, setActiveConversationId, setMessages]);

  // Handle new conversation from history panel
  const handleNewConversation = useCallback(() => {
    setShowHistory(false);
    setActiveConversationId(null);
    clearMessages();
    setPendingHandoff(null);
    setView('selector');
  }, [clearMessages, setActiveConversationId]);

  // Handle archive
  const handleArchive = useCallback(async (id: string) => {
    await archiveConversation(id);
    if (activeConversationId === id) {
      setActiveConversationId(null);
      clearMessages();
      setPendingHandoff(null);
      setView('selector');
    }
  }, [archiveConversation, activeConversationId, clearMessages, setActiveConversationId]);

  // Handle back to selector
  const handleBack = useCallback(() => {
    setView('selector');
    setActiveConversationId(null);
    clearMessages();
    setPendingHandoff(null);
  }, [clearMessages, setActiveConversationId]);

  // Handle close
  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      if (!initialColleagueId) {
        setView('selector');
      }
      clearMessages();
      setActiveConversationId(null);
      setShowHistory(false);
      setPendingHandoff(null);
      lastSavedMessagesRef.current = 0;
    }, 300);
  }, [onClose, clearMessages, initialColleagueId, setActiveConversationId]);

  // Refresh history when modal opens
  useEffect(() => {
    if (open) {
      refreshHistory();
    }
  }, [open, refreshHistory]);

  // Sync suggested page context/colleague whenever a fresh drawer session opens.
  useEffect(() => {
    if (!open) {
      sessionInitializedRef.current = false;
      return;
    }

    if (initialMessage) {
      return;
    }

    setContext(initialContext);

    if (sessionInitializedRef.current) {
      return;
    }

    sessionInitializedRef.current = true;
    if (initialColleagueId) {
      setColleagueId(initialColleagueId);
      setView('chat');
    } else {
      setView('selector');
    }
  }, [open, initialMessage, initialContext, initialColleagueId, setContext, setColleagueId]);

  // Get quick actions for current colleague
  const quickActions = useMemo(() => {
    const actions = QUICK_ACTIONS[colleagueId] || [];

    if (colleagueId === 'jordan' && context?.type === 'instruction') {
      return [
        { label: 'Review this instruction', prompt: 'Can you review this instruction and highlight any concerns?' },
        { label: 'Check for duplicates', prompt: 'Is this instruction a potential duplicate?' },
        { label: 'Draft approval remark', prompt: 'Help me draft an approval remark for this instruction' },
        ...actions
      ];
    }

    if (colleagueId === 'jordan' && context?.type === 'feesPricing') {
      const contextActions = [
        { label: 'Check pricing setup', prompt: 'What should I verify before changing this fee or pricing setup?' },
        { label: 'Fee schedule V2', prompt: 'Explain how Fee Schedule V2 works and what to check before submitting a change.' },
        { label: 'No fee matched', prompt: 'How should I troubleshoot a no matching fee schedule result?' }
      ];
      const contextActionLabels = new Set(contextActions.map((action) => action.label));

      return [
        ...contextActions,
        ...actions.filter((action) => !contextActionLabels.has(action.label))
      ];
    }

    return actions;
  }, [colleagueId, context?.type]);

  // Toggle history panel
  const toggleHistory = useCallback(() => {
    setShowHistory(prev => !prev);
  }, []);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      placement="right"
      width={showHistory ? 900 : 600}
      className={`${styles.modal} ${showHistory ? styles.withHistory : ''}`}
      destroyOnClose={false}
      closable={false}
      mask={false}
      data-testid="digital-staff-drawer"
      styles={{ body: { padding: 0 } }}
    >
      <div className={styles.modalContent}>
        {/* History Panel */}
        {showHistory && (
          <div className={styles.historyPanel}>
            <ConversationHistory
              conversations={conversations}
              isLoading={historyLoading}
              activeId={activeConversationId}
              onSelect={handleLoadConversation}
              onNewConversation={handleNewConversation}
              onArchive={handleArchive}
            />
          </div>
        )}

        {/* Main Content */}
        <div className={styles.mainPanel}>
          {/* Header with history toggle */}
          <div className={styles.modalHeader}>
            <Tooltip title={showHistory ? 'Hide history' : 'Show history'}>
              <Button
                type="text"
                aria-label={showHistory ? 'Hide Digital Staff history' : 'Show Digital Staff history'}
                data-testid="digital-staff-history-toggle"
                icon={<HistoryOutlined />}
                onClick={toggleHistory}
                className={`${styles.historyToggle} ${showHistory ? styles.active : ''}`}
              />
            </Tooltip>
            <div className={styles.headerSpacer} />
            <Button
              type="text"
              aria-label="Close Digital Staff"
              data-testid="digital-staff-close-button"
              icon={<CloseOutlined />}
              onClick={handleClose}
              className={styles.closeButton}
            />
          </div>

          {/* Content */}
          <div className={styles.contentArea}>
            {view === 'selector' ? (
              <ColleagueSelector onSelect={handleSelectColleague} selectedId={colleagueId} />
            ) : (
              <ChatPanel
                colleagueId={colleagueId}
                messages={messages}
                input={input}
                isLoading={isLoading}
                error={error}
                context={context}
                attachments={attachments}
                onInputChange={setInput}
                onSend={() => sendMessage()}
                onStop={stop}
                onReload={reload}
                onClear={clearMessages}
                onBack={handleBack}
                onAddAttachments={addAttachments}
                onRemoveAttachment={removeAttachment}
                onSwitchColleague={handleSwitchColleague}
                quickActions={quickActions}
                inputRef={inputRef}
              />
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default AIColleaguesModal;
